import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.js';
import Loader from '../common/Loader.js';
import SelectField from '../common/SelectField.js';
import dashboardService from '../../services/DashboardService.js';
import { DashboardLayout, StatCard, SectionTable, PageHeader } from '../common/page_component/index.js';
import ScanQrStatusButton from '../shipment-qr-status/ScanQrStatusButton.js';
import RefreshingBanner from './components/RefreshingBanner.js';
import DashboardCardRow from './components/DashboardCardRow.js';
import { dashboardCacheKey, readDashboardCache, writeDashboardCache } from '../../utils/dashboardCache.js';
import { useTranslation } from 'react-i18next';
import {
  Building2, Users, CreditCard, MessageSquare, ShieldCheck, FileText,
  Banknote, AlertTriangle, Clock
} from 'lucide-react';

const emptyStats = {
  totalCompanies: 0, totalUsers: 0,
  activeSubscriptions: 0, whatsappCounters: 0, gstVerificationCounters: 0, ewaybillCounters: 0
};
const emptyRevenue = {
  subscriptionRevenue: 0, subscriptionPending: 0,
  whatsappRevenue: 0, whatsappPending: 0,
  gstVerificationRevenue: 0, gstVerificationPending: 0,
  ewaybillRevenue: 0, ewaybillPending: 0,
  totalRevenue: 0, totalPending: 0
};
const emptyPaymentStatus = {
  subscriptionPaid: 0, subscriptionDue: 0,
  whatsappPaid: 0, whatsappDue: 0,
  gstVerificationPaid: 0, gstVerificationDue: 0,
  ewaybillPaid: 0, ewaybillDue: 0
};

const MasterAdminDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const initialRef = useRef(null);
  if (initialRef.current == null) {
    initialRef.current = readDashboardCache(dashboardCacheKey('master', user, { company: 'all' }));
  }
  const initialCache = initialRef.current;
  const [loading, setLoading] = useState(!initialCache);
  const [refreshing, setRefreshing] = useState(Boolean(initialCache));
  const [companies, setCompanies] = useState(initialCache?.companies || []);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const companiesRef = useRef(companies);
  companiesRef.current = companies;

  const [stats, setStats] = useState(initialCache?.stats || emptyStats);
  const [revenue, setRevenue] = useState(initialCache?.revenue || emptyRevenue);
  const [paymentStatus, setPaymentStatus] = useState(initialCache?.paymentStatus || emptyPaymentStatus);

  const [expiryAlerts, setExpiryAlerts] = useState(initialCache?.expiryAlerts || []);
  const [whatsappSummary, setWhatsappSummary] = useState(initialCache?.whatsappSummary || []);
  const [gstVerificationSummary, setGstVerificationSummary] = useState(initialCache?.gstVerificationSummary || []);
  const [ewaybillSummary, setEwaybillSummary] = useState(initialCache?.ewaybillSummary || []);
  const [recentSubPayments, setRecentSubPayments] = useState(initialCache?.recentSubPayments || []);
  const [recentWaPayments, setRecentWaPayments] = useState(initialCache?.recentWaPayments || []);
  const [recentGstPayments, setRecentGstPayments] = useState(initialCache?.recentGstPayments || []);
  const [recentEwbPayments, setRecentEwbPayments] = useState(initialCache?.recentEwbPayments || []);
  const [error, setError] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  const getExpiryBadge = (daysLeft) => {
    if (daysLeft <= 0) return { label: 'Expired', cls: 'bg-red-50 text-red-700 border-red-200' };
    if (daysLeft <= 7) return { label: 'Critical', cls: 'bg-red-50 text-red-700 border-red-200' };
    if (daysLeft <= 14) return { label: 'Warning', cls: 'bg-orange-50 text-orange-700 border-orange-200' };
    return { label: 'OK', cls: 'bg-green-50 text-green-700 border-green-200' };
  };

  const applySnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    if (snapshot.companies) setCompanies(snapshot.companies);
    if (snapshot.stats) setStats(snapshot.stats);
    if (snapshot.revenue) setRevenue(snapshot.revenue);
    if (snapshot.paymentStatus) setPaymentStatus(snapshot.paymentStatus);
    setExpiryAlerts(snapshot.expiryAlerts || []);
    setWhatsappSummary(snapshot.whatsappSummary || []);
    setGstVerificationSummary(snapshot.gstVerificationSummary || []);
    setEwaybillSummary(snapshot.ewaybillSummary || []);
    setRecentSubPayments(snapshot.recentSubPayments || []);
    setRecentWaPayments(snapshot.recentWaPayments || []);
    setRecentGstPayments(snapshot.recentGstPayments || []);
    setRecentEwbPayments(snapshot.recentEwbPayments || []);
  }, []);

  useEffect(() => {
    document.title = 'System Admin Dashboard';
    const cacheKey = dashboardCacheKey('master', user, { company: selectedCompanyId || 'all' });
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      applySnapshot(cached);
      setLoading(false);
      setRefreshing(true);
    }

    let cancelled = false;
    const fetchDashboardData = async () => {
      try {
        setError(null);
        if (!cached) {
          if (!companiesRef.current.length) setLoading(true);
          else setRefreshing(true);
        } else {
          setRefreshing(true);
        }
        const params = { limit: 5 };
        const company = companiesRef.current.find((c) => c.company_id === selectedCompanyId) || null;
        if (company?.id) params.company_id = company.id;

        const [companiesData, masterData] = await Promise.all([
          dashboardService.getAllCompanies().catch(() => ({})),
          dashboardService.getMasterDashboard(params).catch(() => ({ success: false })),
        ]);
        if (cancelled) return;

        const nextCompanies = companiesData.companies || companiesData.data || [];
        setCompanies(nextCompanies);

        if (!masterData.success) {
          setError('Failed to load dashboard data. Please try again.');
          return;
        }

        const d = masterData.data;
        const snapshot = {
          companies: nextCompanies,
          stats: {
            totalCompanies: d.overview?.total_companies || 0,
            totalUsers: d.overview?.total_users || 0,
            activeSubscriptions: d.overview?.active_subscriptions || 0,
            whatsappCounters: d.overview?.whatsapp_counters || 0,
            gstVerificationCounters: d.overview?.gst_verification_counters || 0,
            ewaybillCounters: d.overview?.ewaybill_counters || 0
          },
          revenue: {
            subscriptionRevenue: d.subscription_revenue?.paid_amount || 0,
            subscriptionPending: d.subscription_revenue?.due_amount || 0,
            whatsappRevenue: d.whatsapp_revenue?.paid_amount || 0,
            whatsappPending: d.whatsapp_revenue?.due_amount || 0,
            gstVerificationRevenue: d.gst_verification_revenue?.paid_amount || 0,
            gstVerificationPending: d.gst_verification_revenue?.due_amount || 0,
            ewaybillRevenue: d.ewaybill_revenue?.paid_amount || 0,
            ewaybillPending: d.ewaybill_revenue?.due_amount || 0,
            totalRevenue: d.revenue?.total_paid || 0,
            totalPending: d.revenue?.total_pending || 0
          },
          paymentStatus: {
            subscriptionPaid: d.payment_status?.subscription_paid || 0,
            subscriptionDue: d.payment_status?.subscription_due || 0,
            whatsappPaid: d.payment_status?.whatsapp_paid || 0,
            whatsappDue: d.payment_status?.whatsapp_due || 0,
            gstVerificationPaid: d.payment_status?.gst_verification_paid || 0,
            gstVerificationDue: d.payment_status?.gst_verification_due || 0,
            ewaybillPaid: d.payment_status?.ewaybill_paid || 0,
            ewaybillDue: d.payment_status?.ewaybill_due || 0
          },
          expiryAlerts: d.expiry_alerts || [],
          whatsappSummary: d.whatsapp_summary || [],
          gstVerificationSummary: d.gst_verification_summary || [],
          ewaybillSummary: d.ewaybill_summary || [],
          recentSubPayments: d.recent_subscription_payments || [],
          recentWaPayments: d.recent_whatsapp_payments || [],
          recentGstPayments: d.recent_gst_verification_payments || [],
          recentEwbPayments: d.recent_ewaybill_payments || [],
        };
        applySnapshot(snapshot);
        writeDashboardCache(cacheKey, snapshot);
      } catch (err) {
        console.error('Dashboard data fetch error:', err);
        if (!cancelled) setError('Failed to load dashboard data. Please try again.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    fetchDashboardData();
    return () => { cancelled = true; };
  }, [selectedCompanyId, user?.user_id, applySnapshot]);

  const handleCompanyChange = (e) => {
    setSelectedCompanyId(e.target.value || null);
  };

  if (loading) return (
    <DashboardLayout>
      <Loader fullScreen={false} message={t('dashboard.loading')} />
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <PageHeader title="System Admin Dashboard" subtitle={`Welcome back, ${user?.name || 'System Admin'}`}>
        <ScanQrStatusButton mobileIconOnly />
        <div className="flex items-center gap-1.5 min-w-0 shrink-0">
          <SelectField
            variant="filter"
            value={selectedCompanyId || ''}
            onChange={handleCompanyChange}
            className="w-auto min-w-[96px] max-w-[132px] sm:min-w-[180px] sm:max-w-none"
          >
            <option value="">{t('common.allCompanies')}</option>
            {companies.map((company) => (
              <option key={company.company_id} value={company.company_id}>
                {company.company_name}
              </option>
            ))}
          </SelectField>
        </div>
      </PageHeader>
      <RefreshingBanner show={refreshing} />

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Key Metrics */}
      <DashboardCardRow>
        <StatCard label="Total Companies" value={stats.totalCompanies} icon={<Building2 className="w-5 h-5 text-white" />} color="bg-blue-600" />
        <StatCard label="Total Users" value={stats.totalUsers} icon={<Users className="w-5 h-5 text-white" />} color="bg-green-600" />
        <StatCard label="Active Subscriptions" value={stats.activeSubscriptions} icon={<CreditCard className="w-5 h-5 text-white" />} color="bg-purple-600" />
        <StatCard label="WhatsApp Counters" value={stats.whatsappCounters} icon={<MessageSquare className="w-5 h-5 text-white" />} color="bg-orange-500" />
        <StatCard label="GST Verification Counters" value={stats.gstVerificationCounters} icon={<ShieldCheck className="w-5 h-5 text-white" />} color="bg-teal-600" />
        <StatCard label="E-Way Bill Counters" value={stats.ewaybillCounters} icon={<FileText className="w-5 h-5 text-white" />} color="bg-indigo-600" />
      </DashboardCardRow>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 md:gap-6 mb-6">
        <RevenueCard
          title="Subscription Revenue" color="bg-purple-500"
          paid={revenue.subscriptionRevenue} pending={revenue.subscriptionPending}
          formatCurrency={formatCurrency}
        />
        <RevenueCard
          title="WhatsApp Payment Revenue" color="bg-orange-500"
          paid={revenue.whatsappRevenue} pending={revenue.whatsappPending}
          formatCurrency={formatCurrency}
        />
        <RevenueCard
          title="GST Verification Revenue" color="bg-teal-600"
          paid={revenue.gstVerificationRevenue} pending={revenue.gstVerificationPending}
          formatCurrency={formatCurrency}
        />
        <RevenueCard
          title="E-Way Bill Revenue" color="bg-indigo-600"
          paid={revenue.ewaybillRevenue} pending={revenue.ewaybillPending}
          formatCurrency={formatCurrency}
        />
        <RevenueCard
          title="Total Revenue" color="bg-green-500"
          paid={revenue.totalRevenue} pending={revenue.totalPending}
          formatCurrency={formatCurrency}
        />
      </div>

      {/* Payment Status */}
      <DashboardCardRow>
        <PaymentStatusCard label="Subscription Paid" amount={paymentStatus.subscriptionPaid} type="success" formatCurrency={formatCurrency} />
        <PaymentStatusCard label="Subscription Due" amount={paymentStatus.subscriptionDue} type="danger" formatCurrency={formatCurrency} />
        <PaymentStatusCard label="WhatsApp Paid" amount={paymentStatus.whatsappPaid} type="success" formatCurrency={formatCurrency} />
        <PaymentStatusCard label="WhatsApp Due" amount={paymentStatus.whatsappDue} type="danger" formatCurrency={formatCurrency} />
        <PaymentStatusCard label="GST Verification Paid" amount={paymentStatus.gstVerificationPaid} type="success" formatCurrency={formatCurrency} />
        <PaymentStatusCard label="GST Verification Due" amount={paymentStatus.gstVerificationDue} type="danger" formatCurrency={formatCurrency} />
        <PaymentStatusCard label="E-Way Bill Paid" amount={paymentStatus.ewaybillPaid} type="success" formatCurrency={formatCurrency} />
        <PaymentStatusCard label="E-Way Bill Due" amount={paymentStatus.ewaybillDue} type="danger" formatCurrency={formatCurrency} />
      </DashboardCardRow>

      {/* Expiry Alerts + WhatsApp Summary */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mb-6">
        <SectionTable
          title="Subscription Expiry Alerts (Next 30 Days)"
          headers={['Company', 'Period', 'Expiry Date', 'Days Left', 'Status']}
          rows={expiryAlerts.map((alert) => {
            const daysLeft = alert.days_left ?? getDaysUntilExpiry(alert.expiry_date);
            const badge = getExpiryBadge(daysLeft);
            return [
              alert.company_name || '-',
              alert.period_type || '-',
              formatDate(alert.expiry_date),
              `${daysLeft} days`,
              <span key="status" className={`px-2 py-0.5 rounded-full text-xs font-medium border ${badge.cls}`}>{badge.label}</span>
            ];
          })}
          emptyMsg="No expiring subscriptions in the next 30 days"
        />
        <SectionTable
          title="WhatsApp Payment Summary (Top Counters)"
          headers={['Company', 'Messages', 'Cost/Msg', 'Total Cost', 'Paid', 'Due']}
          rows={whatsappSummary.map(item => [
            item.company_name || '-',
            item.message_count || 0,
            formatCurrency(item.cost_per_message || 0),
            formatCurrency(item.total_cost || 0),
            formatCurrency(item.paid_amount || 0),
            formatCurrency(item.due_amount || 0),
          ])}
          emptyMsg="No WhatsApp payment data available"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mb-6">
        <SectionTable
          title="GST Verification Payment Summary (Top Counters)"
          headers={['Company', 'Searches', 'Cost/Search', 'Total Cost', 'Paid', 'Due']}
          rows={gstVerificationSummary.map(item => [
            item.company_name || '-',
            item.search_count || 0,
            formatCurrency(item.cost_per_search || 0),
            formatCurrency(item.total_cost || 0),
            formatCurrency(item.paid_amount || 0),
            formatCurrency(item.due_amount || 0),
          ])}
          emptyMsg="No GST verification payment data available"
        />
        <SectionTable
          title="E-Way Bill Payment Summary (Top Counters)"
          headers={['Company', 'API Calls', 'Cost/Call', 'Total Cost', 'Paid', 'Due']}
          rows={ewaybillSummary.map(item => [
            item.company_name || '-',
            item.api_call_count || 0,
            formatCurrency(item.cost_per_call || 0),
            formatCurrency(item.total_cost || 0),
            formatCurrency(item.paid_amount || 0),
            formatCurrency(item.due_amount || 0),
          ])}
          emptyMsg="No e-Way Bill payment data available"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6 mb-6">
        <SectionTable
          title="Recent GST Verification Payments"
          headers={['Company', 'Amount', 'Method', 'Status', 'Date']}
          rows={recentGstPayments.map(p => [
            p.company_name || '-',
            formatCurrency(p.amount),
            p.payment_method || '-',
            p.payment_status || '-',
            formatDate(p.payment_date),
          ])}
          emptyMsg="No recent GST verification payments"
        />
        <SectionTable
          title="Recent E-Way Bill Payments"
          headers={['Company', 'Amount', 'Method', 'Status', 'Date']}
          rows={recentEwbPayments.map(p => [
            p.company_name || '-',
            formatCurrency(p.amount),
            p.payment_method || '-',
            p.payment_status || '-',
            formatDate(p.payment_date),
          ])}
          emptyMsg="No recent e-Way Bill payments"
        />
      </div>

      {/* Recent Payments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <SectionTable
          title="Recent Subscription Payments"
          headers={['Company', 'Amount', 'Method', 'Status', 'Date']}
          rows={recentSubPayments.map(p => [
            p.company_name || '-',
            formatCurrency(p.amount),
            p.payment_method || '-',
            p.payment_status || '-',
            formatDate(p.payment_date),
          ])}
          emptyMsg="No recent subscription payments"
        />
        <SectionTable
          title="Recent WhatsApp Payments"
          headers={['Company', 'Amount', 'Method', 'Status', 'Date']}
          rows={recentWaPayments.map(p => [
            p.company_name || '-',
            formatCurrency(p.amount),
            p.payment_method || '-',
            p.payment_status || '-',
            formatDate(p.payment_date),
          ])}
          emptyMsg="No recent WhatsApp payments"
        />
      </div>
    </DashboardLayout>
  );
};

function RevenueCard({ title, color, paid, pending, formatCurrency }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
        {title}
      </h3>
      <div className="space-y-3">
        <div className="flex justify-between items-end">
          <span className="text-gray-500 text-sm">Paid Amount</span>
          <span className="text-xl font-bold text-green-600">{formatCurrency(paid)}</span>
        </div>
        <div className="border-t border-gray-100 pt-3 flex justify-between items-end">
          <span className="text-gray-500 text-sm">Pending Amount</span>
          <span className="text-xl font-bold text-orange-500">{formatCurrency(pending)}</span>
        </div>
      </div>
    </div>
  );
}

function PaymentStatusCard({ label, amount, type, formatCurrency }) {
  const color = type === 'success' ? 'text-green-600' : 'text-red-500';
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className={`text-xl font-bold mt-1.5 ${color}`}>{formatCurrency(amount)}</p>
    </div>
  );
}

export default MasterAdminDashboard;
